import classNames from "classnames";
import { useTheme } from "PFApp/theme_context_provider/theme_context_provider_context";
import { Inline, Stack } from "PFComponents/containers";
import { Icon } from "PFComponents/icon";
import { Typography } from "PFComponents/typography";
import { ExperienceLevel } from "PFCore/components/experience_level";
import { CurrentAccount, Experience } from "PFCore/types";
import React from "react";
import { useTranslation } from "react-i18next";

import css from "./category_tooltip.module.scss";
import { getAllSkillsFromTree, groupSkillsByExperience, Node } from "./helpers";

type CategoryTooltipProps = {
  color: string;
  data: Node;
  currentAccount: CurrentAccount;
  experienceLevels: Experience[];
  experienceLabelMap: Record<Experience, string>;
};

export const CategoryTooltip = ({
  data,
  color,
  currentAccount,
  experienceLevels,
  experienceLabelMap
}: CategoryTooltipProps) => {
  const { t } = useTranslation("profiles", { keyPrefix: "show.parts" });
  const { themeVars } = useTheme();

  if (typeof document === "undefined") {
    // eslint-disable-next-line react-hooks/immutability -- SSR shim for chart lib's useLayoutEffect
    React.useLayoutEffect = React.useEffect;
  }

  if (!data || !data.data) {
    return null;
  }

  const allSkills = getAllSkillsFromTree(data.data);
  const skillsByExperience = groupSkillsByExperience(allSkills);
  const validatedSkills = allSkills.filter((item) => item.assessed || item.emVerified);
  const subtitle = t("charts.countValidatedSkills", {
    validatedCount: validatedSkills.length,
    allCount: allSkills.length
  });

  const isValidatedSkillsInfoVisible = currentAccount.profile_view_customization.validated_skill_text;

  return (
    <div className={css.hoverWrap}>
      <div className={css.hoverTitle}>
        <div className={css.title}>
          <h4>{data.name}</h4>
          <span>{t("charts.category")}</span>
        </div>
        {isValidatedSkillsInfoVisible && (
          <div className={css.actions}>
            <Icon name="check" color="palettePrimaryAction" size="sm" className={classNames(css.icon)} />
            <p>{subtitle}</p>
          </div>
        )}
      </div>
      <Inline
        style={{ backgroundColor: color }}
        justifyContent="space-around"
        gap="spacingLg"
        className={css.hoverContent}
      >
        {experienceLevels.map((experience) => (
          <Stack key={experience} center>
            <Typography variant="bodyRegular" className={css.experienceLabel} textOverflow="clip">
              {experienceLabelMap[experience]}
            </Typography>
            <ExperienceLevel
              size="md"
              experience={experience}
              color={themeVars["paletteWhite0"]}
              experienceLevels={experienceLevels}
              experienceLabelMap={experienceLabelMap}
              showTooltip={false}
            />
            <Typography variant="bodyRegular">{(skillsByExperience[experience] || []).length}</Typography>
          </Stack>
        ))}
      </Inline>
    </div>
  );
};
